<script setup lang="ts">
import ContentHeader from '@/components/ui/ContentHeader.vue'
import ContentBody from '@/components/ui/ContentBody.vue'
import Card from '@/components/Card/Card.vue'
import { FireRiskDashboardViewModel } from './viewmodels/FireRiskDashboard.viewmodel'
import { FireOutbreakRepositoryApiAdapter } from '@/@core/fire-risk-dashboard/infra/fire-outbreak-repository-adapter/FireOutbreakRepositoryApi.adapter'
import { computed, onMounted, onUnmounted, provide, watch } from 'vue'
import FireRiskByFarmView from './components/views/FireRiskByFarm.view.vue'
import { useWebSocket, useOnline, useIntervalFn, useDocumentVisibility } from '@vueuse/core'
import { NetworkConnectionStatusEnum } from './enums/NetworkConnectionStatus.enum'
import FireAlertMapView from './components/views/FireAlertMap.view.vue'
import FireRiskDashboardHeaderView from './components/views/FireRiskDashboardHeader.view.vue'
import FireAlertsTableView from './components/views/FireAlertsTable.view.vue'
import { fireRiskApi } from '@/services/clients/AxiosClients'
import { MonitoredAreaRepositoryApiAdapter } from '@/@core/monitored-area/infra/monitored-area-repository-adapter/MonitoredAreaRepositoryApi.adapter'
import { RecentFireRiskRepositoryApiAdapter } from '@/@core/fire-risk-dashboard/infra/recent-fire-risk-repository-adapter/RecentFireRiskRepositoryApi.adapter'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'

const monitoredAreaRepository = new MonitoredAreaRepositoryApiAdapter(fireRiskApi)
const recentFireRiskRepository = new RecentFireRiskRepositoryApiAdapter(fireRiskApi)
const fireOutbreakRepository = new FireOutbreakRepositoryApiAdapter(fireRiskApi)

const vm = new FireRiskDashboardViewModel(
	monitoredAreaRepository,
	fireOutbreakRepository,
	recentFireRiskRepository,
)
provide('fire-risk-dashboard-viewmodel', vm)

// A seleção de fazendas fica em localStorage e sobrevive à troca de usuário. O
// descarte tem que acontecer aqui, antes do watch abaixo, senão a página nasce
// buscando os dados das fazendas do cliente anterior.
vm.discardStateFromOtherCustomer()

const customerId = localStorage.getItem('customerId')
const token = localStorage.getItem('jwt')

const { status, data, close } = useWebSocket(`${process.env.VUE_APP_WEBSOCKET_API}/${customerId}`, {
	protocols: [token?.split(' ').at(1) ?? ''],
})

const webSocketConnectionStatus = computed(() =>
	useOnline().value
		? (status.value as NetworkConnectionStatusEnum)
		: NetworkConnectionStatusEnum.CLOSED,
)
watch(data, (message: string) => vm.recieveFireOutbreakAlert(message))

const breakpoints = useBreakpoints(breakpointsTailwind)

const isWide = computed(() => breakpoints['2xl'].value)

// O cálculo do FMA+ é feito em lote pelo serviço de incêndio. Sem atualização
// periódica, o gauge fica congelado no valor do carregamento da página enquanto
// os alertas de foco continuam chegando pelo websocket.
const RECENT_FIRE_RISKS_REFRESH_INTERVAL_MS = 15 * 60 * 1000

let lastRecentFireRisksFetchAt = 0

function refreshRecentFireRisks() {
	lastRecentFireRisksFetchAt = Date.now()
	return vm.getRecentFireRisks()
}

// Sem `immediate`: a primeira busca é disparada pelo loadMonitoredFarms (onMounted),
// que só então tem a seleção validada contra as fazendas do cliente logado.
watch(vm.selectedFarmsIds, () => {
	refreshRecentFireRisks()
	vm.getRecentFireOutbreaks()
})

useIntervalFn(refreshRecentFireRisks, RECENT_FIRE_RISKS_REFRESH_INTERVAL_MS)

const documentVisibility = useDocumentVisibility()

watch(documentVisibility, (visibility) => {
	if (visibility !== 'visible') return
	if (Date.now() - lastRecentFireRisksFetchAt < RECENT_FIRE_RISKS_REFRESH_INTERVAL_MS) return

	refreshRecentFireRisks()
})

onMounted(() => vm.loadMonitoredFarms())

onUnmounted(() => close())
</script>

<template>
	<div>
		<content-header :wide="isWide">
			<fire-risk-dashboard-header-view :websocket-status="webSocketConnectionStatus" />
		</content-header>
		<content-body :wide="isWide">
			<card v-show="vm.showChartSection.value">
				<fire-risk-by-farm-view />
			</card>
			<card v-show="vm.showMapSection.value">
				<fire-alert-map-view />
			</card>
			<card v-show="vm.showTableSection.value">
				<fire-alerts-table-view />
			</card>
		</content-body>
	</div>
</template>

<style scoped>
.container__card {
	width: 100%;
}
</style>
